import "./ScrollyTelling.scss";
import { define, mount } from "@shared/Lib/registry";
import type { Component } from "@shared/Lib/types";
import { ScrollSlide } from "../ScrollSlide/ScrollSlide";
import { SvgBackground } from "../../../Components/Atoms/SvgBackground/SvgBackground";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getViewportHeight } from "../../../Lib/Animations/breakpoints";
import { FADE_HORIZONTAL_STEP_DISTANCE_FACTOR } from "../../../Lib/Animations/helpers";
gsap.registerPlugin(ScrollTrigger);

type BgMode = "none" | "svg";
export const selectors = {
  slides: ".scrollytelling__slides",
  slide: ".scrollytelling__slide",
  content: ".scrollslide",
  bg: ".scrollytelling__bg-svg",
  bgWrapper: ".scrollytelling__bg",
  inner: ".scrollytelling__inner",
} as const;

class Scrollytelling implements Component {
  readonly el: HTMLElement;
  readonly bgMode: BgMode;
  readonly timeline: gsap.core.Timeline = gsap.timeline({
    defaults: { ease: "none" },
  });
  private slides: ScrollSlide[];
  private masterST: ScrollTrigger | null = null;
  private backgroundST: ScrollTrigger | null = null;
  private backgroundHandoffST: ScrollTrigger | null = null;
  private srNavAbort = new AbortController();
  private resizeAbort = new AbortController();
  private resizeRaf = 0;

  /**
   * Track the user's motion preference so we can skip scroll-height
   * compensation (and respond if the preference changes mid-session).
   */
  private readonly prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  constructor(el: HTMLElement) {
    this.el = el;
    this.bgMode = (this.el.dataset.bgMode as BgMode) ?? "none";
    this.slides = [];
    mount(el);
    this.init();
  }

  private init(): void {
    this.updateScrollHeight();
    this.initBackgroundHandoff();
    this.initializeBackground();
    this.initializeChildren();
    this.initMasterPin();
    this.initSrNavigation();
    this.initResizeHandling();
    this.refreshScrollTrigger();
  }
  private getNextScrollytelling(): HTMLElement | null {
    let next = this.el.nextElementSibling as HTMLElement | null;

    while (next) {
      if (next.matches('[data-component="scrollytelling"]')) return next;
      next = next.nextElementSibling as HTMLElement | null;
    }

    return null;
  }

  private initBackgroundHandoff(): void {
    this.backgroundHandoffST?.kill();
    this.backgroundHandoffST = null;

    if (this.bgMode !== "svg" || this.prefersReducedMotion.matches) return;

    const backgroundEl = this.el.querySelector<HTMLElement>(
      selectors.bgWrapper
    );
    const slides = this.el.querySelectorAll<HTMLElement>(selectors.slide);
    const nextScrollytelling = this.getNextScrollytelling();

    if (!backgroundEl || !slides.length || !nextScrollytelling) return;

    const lastStep = Math.max(slides.length - 1, 0);
    let hasTriggered = false;

    this.backgroundHandoffST = ScrollTrigger.create({
      trigger: nextScrollytelling,
      start: "top top",
      onEnter: () => {
        if (hasTriggered) return;
        hasTriggered = true;
        this.el.dispatchEvent(
          new CustomEvent("bgControl", {
            detail: {
              eventName: "morph",
              action: "in",
              step: lastStep,
            },
          })
        );
      },
      onLeaveBack: () => {
        hasTriggered = false;
        this.el.dispatchEvent(
          new CustomEvent("bgControl", {
            detail: {
              eventName: "morph",
              action: "out",
              step: lastStep,
            },
          })
        );
      },
    });
  }

  private hasAnimationType(type: string): boolean {
    return !!this.el.querySelector(
      `${selectors.content}[data-animation-type="${type}"]`
    );
  }

  private refreshScrollTrigger(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    });
  }

  private getFadeHorizontalExtraEndFactor(): number {
    const viewportHeight = getViewportHeight();
    const isLandscape = window.matchMedia("(orientation: landscape)").matches;
    const isWideLandscape =
      isLandscape && window.innerWidth / viewportHeight >= 19 / 6;

    if (isWideLandscape) return 1.05;
    if (isLandscape) return 1.05;
    if (window.innerWidth < 768) return 0.62;

    return 0.62;
  }

  private initMasterPin(): void {
    const slidesEl = this.el.querySelector<HTMLElement>(selectors.slides);
    const innerEl = this.el.querySelector<HTMLElement>(selectors.inner);
    const backgroundEl = this.el.querySelector<HTMLElement>(
      selectors.bgWrapper
    );
    const hasBackground = !!backgroundEl;

    const slides = this.el.querySelectorAll(selectors.slide as string);

    const isNavOnly = Array.from(slides).every((slide) => {
      const content = slide.querySelector<HTMLElement>(".scrollslide__content");
      return !content || content.children.length === 0;
    });

    const pinTarget = hasBackground ? innerEl : slidesEl;

    if (!pinTarget || isNavOnly || this.prefersReducedMotion.matches) {
      this.masterST?.kill();
      this.masterST = null;
      this.backgroundST?.kill();
      this.backgroundST = null;
      return;
    }

    const slideCount = Math.max(slides.length, 1);
    const hasFadeHorizontal = this.hasAnimationType("fade-horizontal");
    const hasParallax = this.hasAnimationType("parallax");

    const viewportHeight = getViewportHeight();
    const isLandscape = window.matchMedia("(orientation: landscape)").matches;
    const isWideLandscape =
      isLandscape && window.innerWidth / window.innerHeight >= 19 / 6;

    const baseExtraEnd = isWideLandscape
      ? viewportHeight
      : isLandscape
        ? viewportHeight * 0.9
        : viewportHeight * 0.4;
    const extraEnd = hasFadeHorizontal
      ? viewportHeight * this.getFadeHorizontalExtraEndFactor()
      : baseExtraEnd;
    const scrollDistance = hasFadeHorizontal
      ? slideCount * viewportHeight * FADE_HORIZONTAL_STEP_DISTANCE_FACTOR
      : slideCount * viewportHeight;
    //const extraEnd = baseExtraEnd;

    this.masterST?.kill();
    this.masterST = ScrollTrigger.create({
      trigger: this.el,
      pin: pinTarget,
      pinSpacing: false,
      start: "top top",
      end: () => `+=${scrollDistance + extraEnd}`,
      scrub: true,
      animation: this.timeline,
      invalidateOnRefresh: true,
    });

    this.backgroundST?.kill();
    this.backgroundST = null;

    if (backgroundEl) {
      this.backgroundST = ScrollTrigger.create({
        trigger: this.el,
        pin: backgroundEl,
        pinSpacing: false,
        start: "top top",
        end: () => `+=${(slideCount + 1) * getViewportHeight()}`,
      });
    }
  }

  private initResizeHandling(): void {
    window.addEventListener(
      "resize",
      () => {
        if (this.resizeRaf) {
          cancelAnimationFrame(this.resizeRaf);
        }

        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = 0;
          this.updateScrollHeight();
          this.initMasterPin();
          this.initBackgroundHandoff();
          this.refreshScrollTrigger();
        });
      },
      { signal: this.resizeAbort.signal }
    );
  }

  private isLastScrollySection = (section: HTMLElement) => {
    const parent = section.parentElement;
    if (!parent) return false;

    const scrollytellingSections = Array.from(parent.children).filter(
      (child): child is HTMLElement => {
        if (
          !(child instanceof HTMLElement) ||
          !child.classList.contains("scrollytelling")
        ) {
          return false;
        }

        const slides = child.querySelectorAll(selectors.slide as string);
        const isNavOnly = Array.from(slides).every((slide) => {
          const content = slide.querySelector<HTMLElement>(
            ".scrollslide__content"
          );
          return !content || content.children.length === 0;
        });

        return !isNavOnly;
      }
    );

    return (
      scrollytellingSections[scrollytellingSections.length - 1] === section
    );
  };

  private updateScrollHeight(): void {
    const slides = this.el.querySelectorAll(selectors.slide as string);

    const isNavOnly = Array.from(slides).every((slide) => {
      const content = slide.querySelector<HTMLElement>(".scrollslide__content");
      return !content || content.children.length === 0;
    });

    if (isNavOnly) {
      const scrollHeight = `2px`;
      this.el.style.setProperty("--scroll-height", scrollHeight);
      this.el.dataset.scrollHeight = scrollHeight;
    } else {
      const viewportHeight = getViewportHeight();
      const slideCount = Math.max(slides.length, 1);
      const hasFadeHorizontal = this.hasAnimationType("fade-horizontal");
      const hasParallax = this.hasAnimationType("parallax");
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;

      const totalScrollScreens = this.prefersReducedMotion.matches
        ? slideCount + 0.8
        : hasFadeHorizontal
          ? slideCount * FADE_HORIZONTAL_STEP_DISTANCE_FACTOR +
            this.getFadeHorizontalExtraEndFactor()
          : hasParallax && isLandscape
            ? slideCount + 1.2
            : slideCount + 0.8;
      const scrollHeight = `${totalScrollScreens * viewportHeight}px`;
      this.el.style.setProperty("--scroll-height", scrollHeight);
      this.el.dataset.scrollHeight = scrollHeight;
    }
  }

  private initializeChildren(): void {
    const slides = this.el.querySelectorAll(selectors.slide as string);
    this.slides = [];

    slides.forEach((slide) => {
      this.slides.push(
        new ScrollSlide(slide as HTMLElement, this.el, this.timeline)
      );
    });
  }

  private initializeBackground(): void {
    const bgEl = this.el.querySelector(selectors.bg!) as HTMLElement;
    if (!bgEl || this.bgMode !== "svg") return;
    new SvgBackground(bgEl, this.el);
  }

  private initSrNavigation(): void {
    this.el
      .querySelectorAll<HTMLButtonElement>(".scrollytelling__sr-next")
      .forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            const stepIndex = parseInt(btn.dataset.scrollToStep ?? "0", 10);
            this.scrollToStep(stepIndex);
          },
          { signal: this.srNavAbort.signal }
        );
      });
  }

  private scrollToStep(stepIndex: number): void {
    const containerTop = this.el.getBoundingClientRect().top + window.scrollY;
    const targetScroll = containerTop + stepIndex * window.innerHeight;

    window.scrollTo({ top: targetScroll, behavior: "smooth" });

    setTimeout(() => {
      const target = this.el.querySelector<HTMLElement>(
        `[data-step="${stepIndex}"]`
      );
      if (!target) return;

      const focusTarget =
        target.querySelector<HTMLElement>("h1,h2,h3,h4,h5,h6") ?? target;

      if (!focusTarget.hasAttribute("tabindex")) {
        focusTarget.setAttribute("tabindex", "-1");
      }
      focusTarget.focus({ preventScroll: true });
    }, 600);
  }

  destroy(): void {
    this.srNavAbort.abort();
    this.resizeAbort.abort();
    this.masterST?.kill();
    this.masterST = null;
    this.backgroundST?.kill();
    this.backgroundST = null;
    this.backgroundHandoffST?.kill();
    this.backgroundHandoffST = null;
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = 0;
    }
  }
}

define("scrollytelling", (el) => new Scrollytelling(el));
